import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Box, Paper, Typography } from "@mui/material";
import { EmailCategory } from "types/generated/models";
import { ROUTES } from "../../routes";
import LinkUnavailable from "../ErrorPages/LinkUnavailable";
import LoadingOverlay from "../Shared/LoadingOverlay";
import SubscriptionInvoicesHeader from "../SubscriptionInvoices/SubscriptionInvoicesHeader";
import EmailPreferencesForm from "./EmailPreferencesForm";
import EmailPreferencesMessage from "./EmailPreferencesMessage";
import { allOff, EmailPreferenceValues, receivesNothing, toPreferenceList } from "./EmailPreferences.helpers";
import { useEmailPreferences } from "./useEmailPreferences";
import { useUpdateEmailPreferences } from "./useUpdateEmailPreferences";
import styles from "./EmailPreferences.module.css";

// Opened from an unsubscribe link in an email. Loading it never changes anything: email security
// scanners open every link, so preferences are saved only from the button.
export default function EmailPreferences() {
    const { token = "" } = useParams<{ token: string }>();
    const { data, isLoading, isNotFound } = useEmailPreferences(token);
    const update = useUpdateEmailPreferences(token);
    const [values, setValues] = useState<EmailPreferenceValues>(allOff);

    useEffect(() => {
        document.title = `${ROUTES.EMAIL_PREFERENCES.pageTitle} - Client Bookings Zanda`;
    }, []);

    if (isLoading) {
        return <LoadingOverlay />;
    }

    if (isNotFound || update.isNotFound || !data) {
        return <LinkUnavailable />;
    }

    const businessName = data.businessName ?? "this practice";

    const renderContent = () => {
        if (!data.isLinkValid || update.isNoLongerValid) {
            return (
                <EmailPreferencesMessage
                    title="This link is no longer valid"
                    text={`Please use the link in the most recent email from ${businessName}.`}
                    dataTestIdSuffix="link_no_longer_valid"
                />
            );
        }

        if (update.isSuccess) {
            return (
                <EmailPreferencesMessage
                    title="Your email preferences have been updated."
                    text={receivesNothing(values) ? `You won't receive emails from ${businessName}.` : undefined}
                    dataTestIdSuffix="updated"
                />
            );
        }

        return (
            <EmailPreferencesForm
                email={data.email ?? ""}
                businessName={businessName}
                values={values}
                isSaving={update.isPending}
                onChange={(category: EmailCategory, receives: boolean) =>
                    setValues((current) => ({ ...current, [category]: receives }))
                }
                onSubmit={() => update.mutate(toPreferenceList(values))}
            />
        );
    };

    return (
        <Box className={styles.page} data-test-id="page_email_preferences">
            <SubscriptionInvoicesHeader logoUrl={data.businessLogoUrl} name={data.businessName} />
            <Box
                className={styles.content}
                sx={(theme) => ({
                    maxWidth: theme.breakpoints.values.md,
                    padding: 4,
                    [theme.breakpoints.down("sm")]: { padding: 2 },
                })}
            >
                <Typography variant="heading4" component="h1">
                    {ROUTES.EMAIL_PREFERENCES.pageTitle}
                </Typography>
                <Paper className={styles.card}>{renderContent()}</Paper>
            </Box>
        </Box>
    );
}
